/* eslint-disable */
// Session 60.24 · WorkspaceFallbackTile
//
// Branded Métier tile that renders in place of a photograph when an
// authorized listing image is not available for a workspace. Per CTO
// directive:
//   - Never imply a photograph of the physical venue
//   - No generic Unsplash / stock photography representing real venues
//   - Automatically replaced the moment `img` is populated
//   - Do NOT mutate workspace records to store a fake image URL
//
// The tile is a self-contained div (not an <img>) so it doesn't share
// the browser's broken-image state. Sized to fill its parent — the
// existing card CSS constrains the aspect ratio.

import Img from "./Img";

const CREAM = "#FAF8F3";
const NAVY = "#1B2340";
const GOLD = "#B8913A";

export default function WorkspaceFallbackTile({
  name,
  location,
  externalBooking = false,
  variant = "card", // "card" | "hero"
  className = "",
  style = {},
}) {
  const isHero = variant === "hero";
  const pad = isHero ? "clamp(28px, 5vw, 48px)" : "18px 20px";
  const eyebrow = isHero ? 13 : 11;
  const title = isHero ? "clamp(28px, 4vw, 40px)" : "clamp(18px, 2vw, 22px)";
  const sub = isHero ? 15 : 12.5;
  const iconSize = isHero ? 72 : 44;

  return (
    <div
      data-testid={`workspace-fallback-tile${name ? `-${String(name).replace(/\s+/g, '-').toLowerCase()}` : ""}`}
      role="img"
      aria-label={name ? `Métier Workspace — ${name}` : "Métier Workspace"}
      className={className}
      style={{
        width: "100%",
        height: "100%",
        minHeight: isHero ? 260 : 160,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "flex-start",
        padding: pad,
        background: CREAM,
        color: NAVY,
        position: "relative",
        overflow: "hidden",
        // Subtle grain — pure CSS, no external asset. Uses layered radial
        // gradients to approximate paper grain without hurting performance.
        backgroundImage:
          "radial-gradient(rgba(27,35,64,0.04) 1px, transparent 1px)," +
          "radial-gradient(rgba(184,145,58,0.05) 1px, transparent 1px)",
        backgroundSize: "6px 6px, 11px 11px",
        backgroundPosition: "0 0, 3px 3px",
        ...style,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Img
          src="/brand/metier-icon.png"
          alt=""
          eager
          style={{
            width: iconSize,
            height: iconSize,
            objectFit: "contain",
            borderRadius: 8,
            background: NAVY,
            padding: iconSize > 60 ? 8 : 5,
          }}
        />
      </div>

      <div style={{ marginTop: isHero ? 24 : 12, width: "100%" }}>
        <div
          style={{
            fontSize: eyebrow,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: GOLD,
            fontWeight: 600,
            marginBottom: 6,
          }}
        >
          Métier Workspace
        </div>
        <div
          style={{
            height: 2,
            width: 32,
            background: GOLD,
            marginBottom: isHero ? 14 : 10,
            opacity: 0.75,
          }}
          aria-hidden="true"
        />
        {name && (
          <div
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: title,
              lineHeight: 1.15,
              color: NAVY,
              fontStyle: "italic",
              fontWeight: 500,
              // Keep to two lines max on cards; hero can breathe.
              display: "-webkit-box",
              WebkitLineClamp: isHero ? 3 : 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              marginBottom: location ? 6 : 0,
            }}
          >
            {name}
          </div>
        )}
        {location && (
          <div
            style={{
              fontSize: sub,
              color: "rgba(27,35,64,0.6)",
              fontWeight: 400,
              letterSpacing: "0.02em",
            }}
          >
            {location}
          </div>
        )}
      </div>

      {externalBooking && (
        <div
          style={{
            position: "absolute",
            right: isHero ? 20 : 14,
            bottom: isHero ? 20 : 14,
            fontSize: 10,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: GOLD,
            fontWeight: 600,
            padding: "4px 8px",
            border: `1px solid ${GOLD}55`,
            borderRadius: 999,
            background: "rgba(184,145,58,0.06)",
          }}
        >
          Partner listing
        </div>
      )}
    </div>
  );
}

// Convenience — mirrors the "does this workspace need the fallback" check
// so call sites stay a single expression.
export function needsFallback(space) {
  if (!space) return false;
  const raw = space.img || space.photo || space.hero_image_url || "";
  return !raw || raw === "null" || raw === "undefined";
}
